import { useTranslation } from "react-i18next";
import { formatEur, formatPercent } from "../../lib/format-kpi";
import type { InvestmentListItem } from "../../lib/investments";

export interface DepotAggregate {
  /** Marktwert ueber alle nicht-skippten Positionen, in CENTS. */
  marketValueEur: number;
  /** Kaufwert ueber alle nicht-skippten Positionen, in CENTS. */
  purchaseValueEur: number;
  /** Tagesveraenderung absolut, in CENTS. */
  dayDiffEur: number;
  /** Tagesveraenderung in Prozent (dayDiff / marketValue * 100). */
  dayDiffPct: number | null;
  /** Veraenderung seit Kauf absolut, in CENTS (marketValue - purchaseValue). */
  totalDiffEur: number;
  /** Veraenderung seit Kauf in Prozent (totalDiff / purchaseValue * 100). */
  totalDiffPct: number | null;
  /** Symbole, deren marketValueEur oder purchaseValueEur null waren. */
  skipped: string[];
}

/**
 * Aggregiert Depot-KPIs ueber die uebergebenen Positionen.
 * Skipped jede Position, deren marketValueEur ODER purchaseValueEur null ist.
 *
 * Achtung: marketValueEur/purchaseValueEur/dailyChangeEur sind trotz
 * "Eur"-Suffix in CENTS — siehe Code-Konvention. Summation in cents, Anzeige
 * via formatEur(...) (teilt selbst durch 100). Bei Diff-Prozenten kuerzt sich
 * der cents/euro-Faktor aus, weil Zaehler und Nenner beide cents sind.
 */
export function computeDepotAggregate(items: InvestmentListItem[]): DepotAggregate {
  const skipped: string[] = [];
  let marketValueEur = 0;
  let purchaseValueEur = 0;
  let dayDiffEur = 0;
  for (const it of items) {
    if (it.calc.marketValueEur === null || it.calc.purchaseValueEur === null) {
      skipped.push(it.symbol);
      continue;
    }
    marketValueEur += it.calc.marketValueEur;
    purchaseValueEur += it.calc.purchaseValueEur;
    dayDiffEur += it.calc.dailyChangeEur ?? 0;
  }
  const totalDiffEur = marketValueEur - purchaseValueEur;
  return {
    marketValueEur,
    purchaseValueEur,
    dayDiffEur,
    dayDiffPct: marketValueEur > 0 ? (dayDiffEur / marketValueEur) * 100 : null,
    totalDiffEur,
    totalDiffPct: purchaseValueEur > 0 ? (totalDiffEur / purchaseValueEur) * 100 : null,
    skipped,
  };
}

/** Signed-EUR-Anzeige fuer cent-Eingaben: prependet "+" wenn positiv (Null und Negativ
 *  uebernehmen das Default-Locale-Verhalten von formatEur). */
function formatSignedEur(cents: number): string {
  const prefix = cents > 0 ? "+" : "";
  return `${prefix}${formatEur(cents)}`;
}

export function DepotKpiCard({ items }: { items: InvestmentListItem[] }) {
  const { t } = useTranslation();
  const a = computeDepotAggregate(items);
  const validCount = items.length - a.skipped.length;
  return (
    <div className="border rounded-lg p-4 shadow-sm bg-white">
      <div className="text-sm text-slate-600 font-medium">{t("investments.kpi.depot.title")}</div>
      <div className="text-xs text-slate-500 mt-1">{t("investments.kpi.depot.subtitle")}</div>
      <div className="text-3xl font-mono font-medium mt-2">{formatEur(a.marketValueEur)}</div>
      <div className="text-xs text-slate-500 font-mono mt-0.5">
        {t("investments.kpi.depot.purchase_label")} {formatEur(a.purchaseValueEur)}
      </div>
      <div className="mt-3 pt-2 border-t border-slate-100 space-y-1 text-xs">
        <DiffLine
          label={t("investments.kpi.depot.vs_yesterday")}
          abs={a.dayDiffEur}
          pct={a.dayDiffPct}
        />
        <DiffLine
          label={t("investments.kpi.depot.vs_purchase")}
          abs={a.totalDiffEur}
          pct={a.totalDiffPct}
        />
      </div>
      {a.skipped.length > 0 && (
        <div className="mt-2 text-[10px] text-amber-700" title={a.skipped.join(", ")}>
          {t("investments.kpi.skipped_badge", {
            n: validCount,
            m: items.length,
          })}
        </div>
      )}
    </div>
  );
}

function DiffLine({
  label,
  abs,
  pct,
}: {
  label: string;
  abs: number;
  pct: number | null;
}) {
  const neg = abs < 0;
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono ${neg ? "text-red-700" : "text-emerald-700"}`}>
        {neg ? "↓" : "↑"} {pct !== null ? `${formatPercent(pct, 2)} │ ` : ""}
        {formatSignedEur(abs)}
      </span>
    </div>
  );
}
